import React, { useState, useEffect } from 'react';
import './App.css';
import { Activity, User, LogOut, Settings, TrendingUp, ShieldCheck, AlertTriangle, Share2, Star, MessageSquare, Award, RefreshCw } from 'lucide-react';

import { api } from './utils/api';
import { formatTime } from './utils/helpers';
import { saveOfflineBank } from './utils/offlineDb';
import { LOCAL_EXERCISES } from './data/exercises';
import { showToast } from './utils/toast';
import { generateFlexCard } from './utils/flexCanvas';

import { useAuth } from './hooks/useAuth';
import { useOfflineSync } from './hooks/useOfflineSync';
import { useWorkout } from './hooks/useWorkout';
import { useSocket } from './hooks/useSocket';

import WorkoutFocus from './components/WorkoutFocus';
import Dashboard from './components/Dashboard';
import Stats from './components/Stats';
import Profile from './components/Profile';
import Admin from './components/Admin';
import Onboarding from './components/Onboarding';
import Builder from './components/Builder';
import Favorites from './components/Favorites';
import RunCoach from './components/RunCoach';
import NetworkIndicator from './components/NetworkIndicator';
import AuthScreen from './components/AuthScreen';
import SwapModal from './components/SwapModal';
import Toast from './components/Toast';
import HumanCoachChat from './components/HumanCoachChat';
import InstallPrompt from './components/InstallPrompt';


function CelebrationScreen({ history, prs, setView, handleShareFlex, isSharing, token, api, showToast }) {
    const [targetWorkout, setTargetWorkout] = React.useState(null);
    const [isSavingFav, setIsSavingFav] = React.useState(false);
    const [hasSavedFav, setHasSavedFav] = React.useState(false);
    const [aiQuote, setAiQuote] = React.useState("");
    const [isFetchingQuote, setIsFetchingQuote] = React.useState(true);
    const [autoTimer, setAutoTimer] = React.useState(5);
    const [timerPaused, setTimerPaused] = React.useState(false);

    React.useEffect(() => {
        if (!targetWorkout && history.length > 0) {
            const newest = history[0];
            const workoutDate = new Date(newest.date || newest.created_at).getTime();
            const ageInMinutes = (Date.now() - workoutDate) / (1000 * 60);

            if (ageInMinutes < 5) {
                setTargetWorkout(newest);
            }
        }
    }, [history, targetWorkout]);

    let totalVolume = 0;
    try {
        const parsedData = typeof targetWorkout?.workout_data === 'string' ? JSON.parse(targetWorkout.workout_data) : (targetWorkout?.workout_data || []);
        if (Array.isArray(parsedData)) {
            parsedData.forEach(ex => {
                if (ex.sets) {
                    ex.sets.forEach(s => {
                        const w = parseFloat(s.weight) || 0;
                        const r = parseInt(s.reps) || 0;
                        if (w > 0 && r > 0) totalVolume += (w * r);
                    });
                }
            });
        }
    } catch(e){}

    const timesCompleted = targetWorkout ? history.filter(h => h.type === targetWorkout.type).length : 0;
    const showSmartFav = timesCompleted >= 3 && !hasSavedFav;

    const wDate = targetWorkout ? new Date(targetWorkout.date || targetWorkout.created_at).toDateString() : '';
    const histPRs = (typeof prs !== 'undefined' ? prs : []).filter(p => new Date(p.date || p.created_at).toDateString() === wDate);

    React.useEffect(() => {
        if (!targetWorkout) return;
        const prNames = histPRs.map(p => p.exercise || p.name);
        fetch('/api/workouts/celebration-quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ type: targetWorkout.type, duration: targetWorkout.duration || 0, volume: totalVolume, prs: prNames })
        }).then(res => res.json()).then(data => {
            setAiQuote(data.quote);
            setIsFetchingQuote(false);
        }).catch(() => {
            setAiQuote("Klaar! Epische prestatie vandaag.");
            setIsFetchingQuote(false);
        });
    }, [targetWorkout, token, histPRs.length]);

    React.useEffect(() => {
        if (timerPaused || !targetWorkout || isFetchingQuote) return;
        if (autoTimer <= 0) {
            setView('dashboard');
            return;
        }
        const int = setInterval(() => setAutoTimer(p => p - 1), 1000);
        return () => clearInterval(int);
    }, [autoTimer, timerPaused, setView, targetWorkout, isFetchingQuote]);

    const saveToFavs = async () => {
        if(!targetWorkout) return;
        setTimerPaused(true);
        setIsSavingFav(true);
        try {
            let actualDescription = targetWorkout.description || '';
            const lines = actualDescription.split('\n');
            if (lines.length > 0 && /^(Score|Tijd|Voltooid):/.test(lines[lines.length - 1])) {
                lines.pop();
                actualDescription = lines.join('\n');
            }
            const wodObj = { title: targetWorkout.type, type: targetWorkout.type, format: 'Uit Logboek', description: actualDescription };
            await api.fetch('/workouts/favorites', { method: 'POST', body: JSON.stringify(wodObj) }, token);
            showToast('Opgeslagen in favorieten! ⭐', 'success');
            setHasSavedFav(true);
        } catch (err) {
            showToast('Fout bij opslaan favoriet', 'error');
        } finally {
            setIsSavingFav(false);
        }
    };

    const formatTimeObj = (seconds) => {
        if (!seconds) return "00:00";
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
    };

    if (!targetWorkout) {
        return (
            <div className="flex flex-col items-center justify-center p-4 min-h-[85vh]">
                <RefreshCw size={50} className="text-orange-500 animate-spin mb-4" />
                <h1 className="text-xl sm:text-2xl font-black text-white uppercase tracking-widest animate-pulse">Workout verwerken...</h1>
                <p className="text-gray-400 mt-2 text-sm">Resultaten worden opgeslagen in je logboek.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center p-4 overflow-hidden animate-fade-in min-h-[85vh] relative select-none">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent pointer-events-none"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[600px] max-h-[600px] bg-orange-600/10 blur-[100px] rounded-full pointer-events-none"></div>

            <div className="relative z-10 flex flex-col items-center text-center max-w-md w-full animate-bounce-in mt-4">
                <h1 className="text-6xl sm:text-7xl font-black uppercase tracking-tighter mb-0 text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500 drop-shadow-2xl">
                    CLEARED
                </h1>
                <h3 className="text-sm text-orange-500 font-black uppercase tracking-widest mb-6">{targetWorkout?.type}</h3>

                {isFetchingQuote ? (
                    <div className="flex flex-col items-center mt-10 mb-10">
                        <RefreshCw size={40} className="text-orange-500 animate-spin mb-4" />
                        <p className="text-orange-400 animate-pulse font-bold tracking-widest uppercase text-sm">MyWOD Coach analyseert prestatie...</p>
                    </div>
                ) : (
                    <div className="w-full flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-zinc-900/80 border border-white/5 rounded-2xl p-4 shadow-2xl backdrop-blur-md flex flex-col items-center justify-center">
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Tijd / Score</p>
                                <p className="text-2xl sm:text-3xl font-black text-white">{targetWorkout?.duration > 0 ? formatTimeObj(targetWorkout.duration) : "Klaar"}</p>
                            </div>
                            <div className="bg-zinc-900/80 border border-white/5 rounded-2xl p-4 shadow-2xl backdrop-blur-md flex flex-col items-center justify-center">
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Totaal Volume</p>
                                <p className="text-2xl sm:text-3xl font-black text-blue-400">{totalVolume > 0 ? totalVolume.toLocaleString() + ' kg' : '-'}</p>
                            </div>
                        </div>

                        {histPRs.length > 0 && (
                            <div className="relative overflow-hidden bg-gradient-to-br from-yellow-700/40 via-yellow-900/50 to-orange-900/40 border-2 border-yellow-500 rounded-2xl p-4 flex flex-col items-center gap-2 shadow-[0_0_40px_rgba(234,179,8,0.3)] w-full mt-2">
                                <div className="absolute inset-0 bg-yellow-500/10 animate-pulse pointer-events-none"></div>
                                <p className="text-yellow-400 text-sm font-black uppercase tracking-widest flex items-center gap-2 drop-shadow-md z-10"><ShieldCheck size={20}/> NIEUWE PR'S GEBROKEN!</p>
                                <div className="flex flex-wrap justify-center gap-2 z-10">
                                    {histPRs.map((pr, i) => (
                                        <span key={i} className="text-xs text-black bg-gradient-to-r from-yellow-400 to-yellow-500 px-2 py-1 rounded-lg font-bold shadow-xl border border-yellow-200">{pr.exercise || pr.name}: <span className="font-black">{pr.weight}kg</span></span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-2 p-5 bg-zinc-900/80 border border-white/5 rounded-2xl relative w-full shadow-2xl backdrop-blur-md">
                            <Award size={28} className="absolute -top-4 left-1/2 -translate-x-1/2 text-orange-500 bg-zinc-950 rounded-full p-1 border border-white/10" />
                            <p className="text-gray-200 italic font-medium leading-relaxed mt-2 text-sm sm:text-base">"{aiQuote}"</p>
                            <p className="text-gray-600 text-[10px] font-black uppercase mt-3 tracking-widest">- MyWOD Coach</p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full">
                    <button onClick={() => { setTimerPaused(true); handleShareFlex(targetWorkout); }} disabled={isSharing} className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black py-3 rounded-xl flex items-center justify-center gap-2 transition-transform hover:scale-105 shadow-[0_0_20px_rgba(147,51,234,0.4)] disabled:opacity-50 text-sm">
                        <Share2 size={18} /> {isSharing ? 'Genereren...' : 'Deel'}
                    </button>
                    <button onClick={() => { setTimerPaused(true); setView('dashboard'); setHasSavedFav(false); }} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl transition-colors border border-zinc-700 text-sm relative overflow-hidden group">
                        <span className="relative z-10">Home ({autoTimer}s)</span>
                        {(!timerPaused && !isFetchingQuote) && <div className="absolute bottom-0 left-0 h-1 bg-orange-500 transition-all ease-linear" style={{ width: `${(autoTimer / 5) * 100}%` }}></div>}
                    </button>
                </div>

                {showSmartFav && (
                    <div className="animate-fade-in mt-3 w-full">
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex flex-col items-center gap-2 shadow-[0_0_20px_rgba(234,179,8,0.15)]">
                            <p className="text-yellow-400 font-bold text-xs">🔥 Je hebt deze workout al {timesCompleted} keer geknald!</p>
                            <button onClick={saveToFavs} disabled={isSavingFav} className="w-full py-3 bg-gradient-to-r from-yellow-600 to-orange-500 hover:from-yellow-500 hover:to-orange-400 text-white font-black rounded-lg transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-md text-xs">
                                <Star size={16} fill="currentColor" className={isSavingFav ? 'animate-pulse' : ''} />
                                {isSavingFav ? 'Bezig met opslaan...' : 'Sla op als Favoriet'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function App() {
    const {
        token, setToken, user, setUser, authMessage, setAuthMessage,
        authMode, setAuthMode, resetEmail, newPassword, setNewPassword,
        handleLogin, handleRegister, handleForgotPassword, handleResetPassword, logout
    } = useAuth();

    const { startLiveWorkout, stopLiveWorkout } = useSocket(token);

    const [view, setView] = useState('dashboard');
    const [history, setHistory] = useState([]);
    const [prs, setPrs] = useState([]);
    const [exerciseDatabase, setExerciseDatabase] = useState([]);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [partyMessage, setPartyMessage] = useState('');
    const [adminTab, setAdminTab] = useState('users');
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [customWods, setCustomWods] = useState([]);
    const [showWodModal, setShowWodModal] = useState(false);
    const [wodFormat, setWodFormat] = useState('AMRAP');
    const [trainingDays, setTrainingDays] = useState(user?.training_days_per_week || 4);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [dataLoaded, setDataLoaded] = useState(false);
    const [onboardingDismissed, setOnboardingDismissed] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    const [isSharing, setIsSharing] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [globalAlert, setGlobalAlert] = useState(null);
    const [hasUnreadChat, setHasUnreadChat] = useState(false);

    const activeTheme = user?.theme || 'dark';
    const isLightMode = Boolean(token && activeTheme === 'light');

    useEffect(() => {
        if (isLightMode) {
            document.body.classList.add('light-theme');
        } else {
            document.body.classList.remove('light-theme');
        }
    }, [isLightMode]);

    useEffect(() => {
        if (!token) {
            setGlobalAlert(null);
            return;
        }

        let mounted = true;
        const loadAlert = async () => {
            try {
                const alertData = await api.fetch('/public/alert', { isLoginAttempt: true }, token);
                if (mounted && alertData && alertData.message) setGlobalAlert(alertData.message);
                else if (mounted) setGlobalAlert(null);
            } catch(e) {}
        };

        loadAlert();
        const interval = setInterval(loadAlert, 60000);
        return () => { mounted = false; clearInterval(interval); };
    }, [token]);

    useEffect(() => {
        if (token) {
            api.fetch('/profile/messages/unread', {}, token)
               .then(data => setHasUnreadChat(data?.hasUnread || false))
               .catch(() => {});
        }
    }, [token, view, isChatOpen]);

    const glassClass = "bg-zinc-900/90 backdrop-blur-xl border border-zinc-700 shadow-2xl rounded-2xl p-6 text-white";
    const btnClass = "px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-lg shadow-md transition-all font-bold tracking-wide flex items-center justify-center gap-2";

    const appBackgroundStyle = isLightMode
        ? { backgroundImage: "radial-gradient(circle at 50% 0%, rgba(251, 146, 60, 0.12), transparent 60%), linear-gradient(to bottom, rgba(248, 250, 252, 0.90), rgba(226, 232, 240, 0.96)), url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1920&auto=format&fit=crop&sat=-100')", backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '100vh' }
        : { backgroundImage: "linear-gradient(to bottom, rgba(24, 24, 27, 0.85), rgba(9, 9, 11, 0.98)), url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1920&auto=format&fit=crop&sat=-100')", backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '100vh' };

    useEffect(() => {
        if (!document.getElementById('mywod-font')) {
            const link = document.createElement('link'); link.id = 'mywod-font'; link.rel = 'stylesheet'; link.href = 'https://fonts.googleapis.com/css2?family=Anton&display=swap'; document.head.appendChild(link);
        }
    }, []);

    const fetchInitialData = async (overrideToken) => {
        const activeToken = overrideToken || token;
        if(!activeToken) return;
        const currentUser = user || JSON.parse(localStorage.getItem('user'));
        try {
            if (!navigator.onLine) throw new Error("Offline modus");
            const histData = await api.fetch('/profile/history', {}, activeToken); setHistory(histData);
            if (currentUser) localStorage.setItem(`history_${currentUser.id}`, JSON.stringify(histData));

            const prData = await api.fetch('/profile/prs', {}, activeToken); setPrs(prData);
            if (currentUser) localStorage.setItem(`prs_${currentUser.id}`, JSON.stringify(prData));

            let exData = []; try { exData = await api.fetch('/exercises/dictionary', {}, activeToken); } catch (e) { exData = await api.fetch('/exercises', {}, activeToken); }
            setExerciseDatabase(exData || []);

            try { const bankData = await api.fetch('/offline-bank', {}, activeToken); if (bankData && bankData.bank) { await saveOfflineBank(bankData.bank); } } catch (e) {}
        } catch (error) {
            if (currentUser) {
                setHistory(JSON.parse(localStorage.getItem(`history_${currentUser.id}`)) || []); setPrs(JSON.parse(localStorage.getItem(`prs_${currentUser.id}`)) || []);
            }
        } finally {
            setDataLoaded(true);
        }
    };

    useOfflineSync(token, fetchInitialData);

    const {
        currentWod, setCurrentWod, liveExercises, setLiveExercises, loggedPRs, setLoggedPRs,
        soundEnabled, setSoundEnabled, isFullscreen, setIsFullscreen, isRecovery, setIsRecovery,
        prModal, setPrModal, swapModal, setSwapModal, swapSearch, setSwapSearch, requestWakeLock, toggleFullScreen, setupTimerForWod, cleanupFocusMode,
        getWod, executeSwap, handleLogWorkout, loadBenchmark, time, setTime, isRunning, setIsRunning, rounds, setRounds, intervalPhase, setIntervalPhase,
        currentIntervalRound, setCurrentIntervalRound, restTime, setRestTime, recordedTimes, setRecordedTimes, activeTimerTask, setActiveTimerTask, timerSeconds, setTimerSeconds, timerRunning, setTimerRunning
    } = useWorkout({ token, user, exerciseDatabase, fetchInitialData, setView, setPartyMessage, setShowConfirmModal, prs, history });

    useEffect(() => { if (token && !dataLoaded) { fetchInitialData(token); } const timer = setTimeout(() => setIsInitializing(false), 500); return () => clearTimeout(timer); }, [token, dataLoaded]);

    const onLoginSubmit = (e) => handleLogin(e, fetchInitialData, setTrainingDays);

    const updateUserSettings = async (updatedFields) => {
        try {
            const newUserData = { ...user, ...updatedFields };
            await api.fetch('/profile/settings', { method: 'PUT', body: JSON.stringify(newUserData) }, token);
            setUser(newUserData);
            localStorage.setItem('user', JSON.stringify(newUserData));

            const wodAffectingKeys = ['level', 'training_location', 'training_type', 'home_equipment', 'gender', 'age'];
            if (Object.keys(updatedFields || {}).some(k => wodAffectingKeys.includes(k))) {
                setCurrentWod(null);
            }
            showToast("Profiel geüpdatet!", "success");
        } catch (err) {
            showToast(`Fout bij updaten: ${err.message}`, "error");
        }
    };

    const updateTrainingDays = async (val) => { setTrainingDays(val); await updateUserSettings({ training_days_per_week: val }); };

    const deleteWorkout = async (id) => { if (!window.confirm("Weet je zeker dat je deze workout wilt verwijderen?")) return; try { await api.fetch(`/workouts/${id}`, { method: 'DELETE' }, token); fetchInitialData(); showToast("Workout verwijderd", "success"); } catch (err) { showToast("Verwijderen mislukt.", "error"); } };
    const deletePR = async (exercise) => { if (!window.confirm(`Weet je zeker dat je het PR voor ${exercise} wilt verwijderen?`)) return; try { await api.fetch(`/profile/prs/${encodeURIComponent(exercise)}`, { method: 'DELETE' }, token); fetchInitialData(); showToast("PR verwijderd", "success"); } catch (err) { showToast("Verwijderen PR mislukt.", "error"); } };

    const fetchAdminData = async () => {
        try {
            const uData = await api.fetch('/admin/users', {}, token);
            setAdminUsers(uData);
            if (user) {
                const updatedMe = uData.find(u => u.id === user.id);
                if (updatedMe) {
                    const mergedMe = { ...user, ...updatedMe, theme: updatedMe.theme || user.theme || 'dark' };
                    setUser(mergedMe);
                    localStorage.setItem('user', JSON.stringify(mergedMe));
                }
            }
            const wData = await api.fetch('/admin/wods', {}, token);
            setCustomWods(wData);
        } catch (error) {}
    };

    const handleShareFlex = async (customWorkout = null) => {
        setIsSharing(true);
        try {
            const isEvent = customWorkout && customWorkout.nativeEvent;
            const targetWorkout = (customWorkout && !isEvent) ? customWorkout : (history && history.length > 0 ? history[0] : null);
            let prMsg = (customWorkout && !isEvent) ? null : partyMessage;
            if (customWorkout && !isEvent) {
                const wDate = new Date(targetWorkout.date || targetWorkout.created_at).toDateString();
                const histPRs = (typeof prs !== 'undefined' ? prs : []).filter(p => new Date(p.date || p.created_at).toDateString() === wDate);

                if (histPRs.length > 0) {
                    prMsg = "NIEUW PR GEZET!\n\n" + histPRs.map(pr => `${pr.exercise || pr.name}: ${pr.weight}${pr.unit ? pr.unit.toLowerCase() : 'kg'}`).join('\n');
                }
            }
            const blob = await generateFlexCard(user, prMsg, targetWorkout);

            const dateStr = new Date().toISOString().split('T')[0];
            const uniqueId = Math.floor(Math.random() * 10000);
            const fileName = `mywod-result-${dateStr}-${uniqueId}.png`;

            const file = new File([blob], fileName, { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ title: 'Mijn WOD Resultaat', text: 'Kapot gegaan, maar overleefd! 💪 #MyWoD', files: [file] });
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = fileName; a.click(); URL.revokeObjectURL(url);
                showToast('Plaatje opgeslagen! Deel hem zelf.', 'success');
            }
        } catch (error) { showToast('Delen mislukt of geannuleerd.', 'error'); } finally { setIsSharing(false); }
    };

    const groups = { Warmup: [], Training: {}, Cardio: [] };
    if (currentWod?.isFitness && Array.isArray(liveExercises)) {
        liveExercises.forEach((ex, idx) => {
            if (!ex) return;
            const safeName = String(ex.cleanName || ex.name || 'Oefening').replace(/^cardio:\s*/i, '').trim();
            let cleanName = safeName;
            const nameLower = safeName.toLowerCase();

            let isWarmup = idx === 0 && (nameLower.includes('warm') || (nameLower.includes('roeien') && !ex.isCardioBlock));
            let isCardio = !!ex.isCardioBlock || nameLower.includes('cardio') || nameLower.includes('finisher');
            if (!isWarmup && !isCardio && (nameLower.includes('roeien') || nameLower.includes('fietsen') || nameLower.includes('rennen') || nameLower.includes('hardlopen') || nameLower.includes('loopband') || nameLower.includes('assault') || nameLower.includes('skierg') || nameLower.includes('crosstrainer'))) {
                if (idx === 0 && !currentWod.isCustomBuilt) isWarmup = true;
                else isCardio = true;
            }

            let letter = '';
            let fullDisplayCode = ex.displayCode ? String(ex.displayCode).toUpperCase() : '';

            if (!isWarmup && !isCardio) {
                if (ex.order) {
                    letter = String(ex.order).charAt(0).toUpperCase();
                    if (!fullDisplayCode) fullDisplayCode = String(ex.order).toUpperCase();
                } else if (ex.displayCode) {
                    letter = String(ex.displayCode).charAt(0).toUpperCase();
                } else {
                    const match = cleanName.match(/^([A-Z])(\d)?[\s\-:.)]+(.*)/i);
                    if (match) {
                        letter = match[1].toUpperCase();
                        fullDisplayCode = match[1].toUpperCase() + (match[2] || '');
                        cleanName = match[3].trim();
                    } else {
                        letter = String.fromCharCode(65 + idx);
                        fullDisplayCode = letter;
                    }
                }
            } else {
                const match = cleanName.match(/^([A-Z])(\d)?[\s\-:.)]+(.*)/i);
                if (match) cleanName = match[3].trim();
            }

            const blockKey = letter || 'A';
            const parsedEx = {
                ...ex,
                originalIdx: ex.originalIdx !== undefined ? ex.originalIdx : idx,
                cleanName: cleanName,
                displayCode: fullDisplayCode || blockKey
            };

            if (isWarmup) {
                groups.Warmup.push(parsedEx);
            } else if (isCardio) {
                groups.Cardio.push(parsedEx);
            } else {
                if (!groups.Training[blockKey]) {
                    groups.Training[blockKey] = [parsedEx];
                } else {
                    groups.Training[blockKey].push(parsedEx);
                }
            }
        });
    }

    if (isInitializing) {
        return (
            <div style={appBackgroundStyle} className="min-h-screen flex items-center justify-center p-4">
                <div className={`${glassClass} flex flex-col items-center justify-center gap-6 w-full max-w-sm`}>
                    <img src="/logo192.png" alt="MyWoD" className="w-16 h-16 animate-pulse drop-shadow-[0_0_15px_rgba(234,88,12,0.5)]" />
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-orange-500 font-black tracking-widest uppercase text-sm animate-pulse">Verbinding maken...</p>
                </div>
            </div>
        );
    }

    const isImpersonating = !!localStorage.getItem('admin_token_backup');

    const handleExitImpersonation = () => {
        const adminToken = localStorage.getItem('admin_token_backup');
        const adminUser = localStorage.getItem('admin_user_backup');

        if (adminToken && adminUser) {
            localStorage.setItem('token', adminToken);
            localStorage.setItem('user', adminUser);
            localStorage.removeItem('admin_token_backup');
            localStorage.removeItem('admin_user_backup');
            window.location.href = '/';
        }
    };

    if (!token) {
        return (
            <>
                <AuthScreen authMode={authMode} setAuthMode={setAuthMode} authMessage={authMessage} setAuthMessage={setAuthMessage} handleLogin={onLoginSubmit} handleRegister={handleRegister} handleForgotPassword={handleForgotPassword} handleResetPassword={handleResetPassword} resetEmail={resetEmail} newPassword={newPassword} setNewPassword={setNewPassword} appBackgroundStyle={appBackgroundStyle} glassClass={glassClass} />
                <InstallPrompt view={view} />
                <Toast />
            </>
        );
    }

    if (token && (!user || !dataLoaded)) {
        return (
            <div style={appBackgroundStyle} className="min-h-screen flex items-center justify-center p-4">
                <div className={`${glassClass} flex flex-col items-center justify-center gap-6 w-full max-w-sm`}>
                    <img src="/logo192.png" alt="MyWoD" className="w-16 h-16 animate-pulse drop-shadow-[0_0_15px_rgba(234,88,12,0.5)]" />
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-orange-500 font-black tracking-widest uppercase text-sm animate-pulse">Systeem laden...</p>
                </div>
            </div>
        );
    }

    if (user && user.deletion_scheduled_at) {
        return (
            <div style={appBackgroundStyle} className="p-4 sm:p-8 min-h-screen flex flex-col items-center justify-center relative">
                 <div className={`${glassClass} max-w-lg w-full text-center space-y-6 animate-fade-in`}>
                      <AlertTriangle size={80} className="text-red-500 mx-auto drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" />
                      <div>
                          <h2 className="text-3xl font-black text-white uppercase tracking-wider mb-2">Account Verwijdering</h2>
                          <p className="text-gray-400 text-sm leading-relaxed">Welkom terug! Je hebt aangegeven je account te willen verwijderen. Dit staat gepland om over enkele dagen <strong>definitief en onherstelbaar</strong> te gebeuren.</p>
                      </div>
                      <div className="bg-black/30 border border-white/10 rounded-xl p-4 text-orange-400 font-bold">Je bent nog binnen de 5 dagen bedenktijd. Wil je dit proces nu annuleren en je account behouden?</div>
                      <div className="flex flex-col sm:flex-row gap-4 mt-6">
                           <button onClick={async () => { try { await api.fetch('/profile/cancel-delete', { method: 'POST' }, token); setUser({ ...user, deletion_scheduled_at: null }); showToast('Account is succesvol hersteld!', 'success'); } catch(e) { showToast('Fout bij annuleren', 'error'); } }} className="flex-1 bg-green-600 hover:bg-green-500 text-white py-4 rounded-xl font-black uppercase tracking-widest transition-colors shadow-lg shadow-green-900/20">Ja, behoud account</button>
                           <button onClick={logout} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-gray-400 py-4 rounded-xl font-bold uppercase tracking-widest transition-colors border border-white/5">Nee, log uit</button>
                      </div>
                 </div>
            </div>
        );
    }

    const needsOnboarding = user && dataLoaded && history.length === 0 && !onboardingDismissed && !localStorage.getItem(`onboarding_done_${user.id}`) && view !== 'focus' && view !== 'celebration' && view !== 'builder' && navigator.onLine;

    return (
        <div style={appBackgroundStyle} className={`p-4 sm:p-8 min-h-screen relative overflow-hidden ${isImpersonating ? "pt-16 sm:pt-16" : ""}`}>

            {isImpersonating && (
                <div className="fixed top-0 left-0 right-0 z-[999999] bg-red-600 text-white font-bold py-2.5 px-4 flex flex-col sm:flex-row justify-center items-center gap-3 shadow-2xl border-b border-red-400">
                    <span className="flex items-center gap-2 text-sm sm:text-base animate-pulse font-black tracking-wide">
                        ⚠️ Meekijkmodus actief: <span className="underline">{user?.name}</span>
                    </span>
                    <button
                        onClick={handleExitImpersonation}
                        className="bg-white text-red-600 px-4 py-1 rounded-full text-xs sm:text-sm font-black hover:bg-gray-100 transition-all shadow-md active:scale-95"
                    >
                        Sluit Meekijken & Keer Terug naar Admin
                    </button>
                </div>
            )}

            <NetworkIndicator />
            {globalAlert && (
                <div className="bg-red-600 text-white font-black text-center py-2 px-4 rounded-xl shadow-lg mb-4 flex items-center justify-center gap-2 animate-pulse border border-white/20 relative z-20">
                    <AlertTriangle size={18} /> {globalAlert}
                </div>
            )}

            {needsOnboarding && (
                <Onboarding user={user} updateUserSettings={updateUserSettings} updateTrainingDays={updateTrainingDays} api={api} token={token} btnClass={btnClass} onComplete={() => { localStorage.setItem(`onboarding_done_${user.id}`, 'true'); setOnboardingDismissed(true); }} />
            )}

            {view !== 'focus' && view !== 'celebration' && view !== 'builder' && (
                <nav className={glassClass + " flex flex-wrap justify-between items-center mb-8 py-4 gap-4 mt-6 relative z-10"}>
                    <div className="flex items-center gap-4">
                        <img src="/logo192.png" alt="MyWoD" className="w-12 h-12 object-contain drop-shadow-md" onError={(e) => { e.target.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`}} />
                        <div className="flex flex-col">
                            <span className="text-2xl sm:text-3xl text-white uppercase leading-none font-black tracking-tight" style={{ fontFamily: '"Anton", Impact, sans-serif', letterSpacing: '0.05em' }}>MYWOD</span>
                            <span className="text-[10px] sm:text-xs text-orange-500 uppercase tracking-widest font-black mt-0.5">Workout of the Day</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                        <button onClick={() => { setView('dashboard'); setCurrentWod(null); }} className={`flex items-center gap-1 font-bold transition-colors ${view === 'dashboard' ? 'text-orange-500' : 'text-zinc-500 hover:text-orange-400'}`}><Activity size={18}/> Home</button>
                        <button onClick={() => { setView('stats'); fetchInitialData(); }} className={`flex items-center gap-1 font-bold transition-colors ${view === 'stats' ? 'text-orange-500' : 'text-zinc-500 hover:text-orange-400'}`}><TrendingUp size={18}/> Stats</button>
                        <button onClick={() => { setView('profile'); fetchInitialData(); }} className={`flex items-center gap-1 font-bold transition-colors ${view === 'profile' ? 'text-orange-500' : 'text-zinc-500 hover:text-orange-400'}`}><User size={18}/> Profiel</button>
                        {user?.role === 'admin' && <button onClick={() => { setView('admin'); fetchAdminData(); }} className={`flex items-center gap-1 font-bold transition-colors ${view === 'admin' ? 'text-orange-500' : 'text-zinc-500 hover:text-orange-400'}`}><Settings size={18}/> Admin</button>}
                        <button onClick={isImpersonating ? handleExitImpersonation : logout} title={isImpersonating ? "Terug naar Admin" : "Uitloggen"} className="ml-2 text-zinc-600 hover:text-red-500 transition-colors"><LogOut size={20} /></button>
                    </div>
                </nav>
            )}

            {view === 'celebration' && <CelebrationScreen history={history} prs={prs} setView={setView} handleShareFlex={handleShareFlex} isSharing={isSharing} token={token} api={api} showToast={showToast} />}

            {view === 'focus' && (<WorkoutFocus exerciseDatabase={exerciseDatabase} prs={prs} user={user} currentWod={currentWod} liveExercises={liveExercises} recordedTimes={recordedTimes} setRecordedTimes={setRecordedTimes} activeTimerTask={activeTimerTask} setActiveTimerTask={setActiveTimerTask} timerSeconds={timerSeconds} setTimerSeconds={setTimerSeconds} timerRunning={timerRunning} setTimerRunning={setTimerRunning} formatTime={formatTime} toggleFullScreen={toggleFullScreen} isFullscreen={isFullscreen} intervalPhase={intervalPhase} restTime={restTime} setRestTime={setRestTime} time={time} setTime={setTime} isRunning={isRunning} setIsRunning={setIsRunning} soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled} rounds={rounds} setRounds={setRounds} currentIntervalRound={currentIntervalRound} groups={groups} history={history} setPrModal={setPrModal} loggedPRs={loggedPRs} cleanupFocusMode={cleanupFocusMode} setView={setView} showConfirmModal={showConfirmModal} setShowConfirmModal={setShowConfirmModal} handleLogWorkout={handleLogWorkout} prModal={prModal} setSwapModal={setSwapModal} startLiveWorkout={startLiveWorkout} stopLiveWorkout={stopLiveWorkout} />)}
            {view === 'dashboard' && (<Dashboard user={user} currentWod={currentWod} history={history} trainingDays={trainingDays} getWod={getWod} isRecovery={isRecovery} liveExercises={liveExercises} groups={groups} exerciseDatabase={exerciseDatabase} LOCAL_EXERCISES={LOCAL_EXERCISES} requestWakeLock={requestWakeLock} setCurrentWod={setCurrentWod} setupTimerForWod={setupTimerForWod} setView={setView} btnClass={btnClass} setSwapModal={setSwapModal} handleShareFlex={handleShareFlex} />)}
            {view === 'builder' && (<Builder user={user} setView={setView} setupTimerForWod={setupTimerForWod} api={api} token={token} exerciseDatabase={exerciseDatabase} fetchInitialData={fetchInitialData} requestWakeLock={requestWakeLock} setCurrentWod={setCurrentWod} />)}
            {view === 'running' && (<RunCoach user={user} api={api} token={token} glassClass={glassClass} setView={setView} setCurrentWod={setCurrentWod} setupTimerForWod={setupTimerForWod} requestWakeLock={requestWakeLock} />)}
            {view === 'favorites' && (<Favorites user={user} setView={setView} api={api} token={token} setCurrentWod={setCurrentWod} setupTimerForWod={setupTimerForWod} requestWakeLock={requestWakeLock} glassClass={glassClass} />)}
            {view === 'stats' && (<Stats user={user} history={history} prs={prs} deletePR={deletePR} fetchInitialData={fetchInitialData} glassClass={glassClass} exerciseDatabase={exerciseDatabase} api={api} token={token} />)}
            {view === 'profile' && (<Profile user={user} history={history} fetchInitialData={fetchInitialData} showAllHistory={showAllHistory} setShowAllHistory={setShowAllHistory} loadBenchmark={loadBenchmark} deleteWorkout={deleteWorkout} updateUserSettings={updateUserSettings} trainingDays={trainingDays} setTrainingDays={setTrainingDays} updateTrainingDays={updateTrainingDays} api={api} token={token} btnClass={btnClass} glassClass={glassClass} setCurrentWod={setCurrentWod} setupTimerForWod={setupTimerForWod} setView={setView} requestWakeLock={requestWakeLock} handleShareFlex={handleShareFlex} />)}
            {view === 'admin' && (<Admin adminTab={adminTab} setAdminTab={setAdminTab} adminUsers={adminUsers} selectedUser={selectedUser} setSelectedUser={setSelectedUser} customWods={customWods} showWodModal={showWodModal} setShowWodModal={setShowWodModal} wodFormat={wodFormat} setWodFormat={setWodFormat} api={api} token={token} fetchAdminData={fetchAdminData} glassClass={glassClass} btnClass={btnClass} />)}

            {((['stats', 'profile'].includes(view)) || (view === 'dashboard' && !currentWod)) && (
                <button onClick={() => setIsChatOpen(true)} className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-40 bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 rounded-full shadow-[0_0_20px_rgba(234,88,12,0.4)] hover:scale-110 hover:shadow-[0_0_30px_rgba(234,88,12,0.6)] transition-all flex items-center justify-center group">
                    <MessageSquare size={26} />
                    {hasUnreadChat && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                        </span>
                    )}
                </button>
            )}

            {user && (
                <HumanCoachChat isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} token={token} />
            )}

            <SwapModal swapModal={swapModal} setSwapModal={setSwapModal} swapSearch={swapSearch} setSwapSearch={setSwapSearch} exerciseDatabase={exerciseDatabase} executeSwap={executeSwap} />
            <Toast />
        </div>
    );
}
